/**
 * "¿Te sirvió?" debajo de los resultados (tarea D2 del vault 127).
 *
 * ⚖️ Lo que vale de esto no es el pulgar: es el **código correcto**. Una consulta
 * que falló, junto con la partida que sí era, es exactamente un caso de prueba —
 * y hoy los 30 casos de `lista-control.json` se escriben a mano. Sin esta caja,
 * el motor solo mejora contra los casos que alguien se acordó de anotar.
 *
 * Por eso el 👎 despliega el pedido de "¿cuál era la correcta?" y el 👍 no
 * pregunta nada: pedirle datos a quien ya está conforme solo consigue que no
 * vuelva a calificar.
 */
import { useEffect, useState } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/lib/hooks/use-toast';
import { tariffCatalogService, type Calificacion } from '../services/tariff-catalog-service';

export function CalificarBusqueda({ busquedaId }: { busquedaId: string }) {
  const { toast } = useToast();
  const [elegida, setElegida] = useState<Calificacion | null>(null);
  const [comentario, setComentario] = useState('');
  const [codigoCorrecto, setCodigoCorrecto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState(false);

  // Otra búsqueda es otra pregunta: el control tiene que volver a cero, o el
  // usuario vería su respuesta anterior pegada a resultados nuevos.
  useEffect(() => {
    setElegida(null);
    setComentario('');
    setCodigoCorrecto('');
    setListo(false);
  }, [busquedaId]);

  const enviar = async (calificacion: Calificacion, conDetalle = false) => {
    setEnviando(true);
    try {
      await tariffCatalogService.calificarBusqueda(busquedaId, {
        calificacion,
        ...(conDetalle && comentario.trim() ? { comentario: comentario.trim() } : {}),
        ...(conDetalle && codigoCorrecto.trim() ? { codigoCorrecto: codigoCorrecto.trim() } : {}),
      });
      setListo(true);
    } catch (e: unknown) {
      // No se traga el error: el usuario hizo clic esperando que se guarde, y un
      // "gracias" mentiroso es peor que no tener el control.
      toast({
        title: 'No pudimos guardar tu respuesta',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setEnviando(false);
    }
  };

  const elegir = (calificacion: Calificacion) => {
    setElegida(calificacion);
    // El 👍 se manda solo. El 👎 espera: primero se le pregunta cuál era la
    // correcta, que es el dato que sirve.
    if (calificacion === 'UTIL') void enviar('UTIL');
  };

  if (listo) {
    return (
      <p className="text-sm text-muted-foreground">
        Gracias — con esto el buscador mejora para la próxima.
      </p>
    );
  }

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm">¿Te sirvió?</span>
        <Button
          type="button"
          size="sm"
          variant={elegida === 'UTIL' ? 'default' : 'outline'}
          disabled={enviando}
          onClick={() => elegir('UTIL')}
        >
          <ThumbsUp className="mr-1.5 h-3.5 w-3.5" />
          Sí
        </Button>
        <Button
          type="button"
          size="sm"
          variant={elegida === 'NO_UTIL' ? 'default' : 'outline'}
          disabled={enviando}
          onClick={() => elegir('NO_UTIL')}
        >
          <ThumbsDown className="mr-1.5 h-3.5 w-3.5" />
          No
        </Button>
      </div>

      {elegida === 'NO_UTIL' && (
        <div className="space-y-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="cal-codigo">
              ¿Cuál era la partida correcta? (si la sabés)
            </label>
            <Input
              id="cal-codigo"
              className="font-mono"
              placeholder="6403.40.00.00"
              value={codigoCorrecto}
              onChange={(e) => setCodigoCorrecto(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="cal-comentario">
              ¿Qué esperabas encontrar?
            </label>
            <Textarea
              id="cal-comentario"
              rows={2}
              placeholder="Buscaba botas de seguridad y me trajo latas de pintura."
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
            />
          </div>
          {/* Se puede mandar sin llenar nada: obligar a explicar consigue que la
              gente no califique, y un "no me sirvió" pelado ya es información. */}
          <Button
            type="button"
            size="sm"
            disabled={enviando}
            onClick={() => enviar('NO_UTIL', true)}
          >
            Enviar
          </Button>
        </div>
      )}
    </div>
  );
}
